<template lang="html">
  <div class="table">
    <div class="table-container">
      <div class="inner-scroll">
        <table :class="{'is-fixed': fixedColumns}">
          <thead>
            <td v-for="(heading, index) in columnHeadings" :key="randomKey(index)" :class="headingClass(heading)">
              {{ heading }}
            </td>
          </thead>

          <draggable
            :list="rows"
            @start="activateDrag"
            @end="orderRows(rows)"
            v-bind="dragOptions"
            class="sortable-wrapper"
            :class="{ 'is-sortable': sortable }"
            tag="tbody"
            handle=".drag-handle">

            <transition-group
              type="transition"
              tag="div"
              class="rows-wrapper"
              :name="!drag ? 'flip-list' : null">

              <tr
                @click="onRowClick(row)"
                v-for="(row, rowIndex) in rows"
                :key="`row-${rowIndex}`">
                <td v-for="key in columnKeys" :key="key">
                  {{ row[key] }}
                  <slot :name="'td-' + rowIndex + '-' + key"></slot>
                </td>
                <td class="drag-handle">
                  <div class="action">
                    <button title="Reorder">
                      <Sprite name="drag"/>
                    </button>
                  </div>
                </td>
              </tr>

            </transition-group>

          </draggable>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import Sprite from '@/components/Sprite'

/**
 * Map Column `headings` by `key` to be used in table
 *
 * @param {array} headings - headings prop
 * @param {string} key - Key to use from heading objects
 * @returns {array} - Mapped headings
 */
function mapHeadings(headings, key) {
  return headings.map((h) => {
    if (typeof h === 'string') {
      return h
    } else {
      return h[key]
    }
  })
}

export default {
  name: 'Table',

  props: {
    fixedColumns: {
      type: Boolean,
      default: false
    },

    rows: {
      type: Array,
      required: true
    },

    headings: {
      type: Array,
      required: true
    },

    sortable: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      drag: false
    }
  },

  components: {
    Sprite,
    draggable
  },

  computed: {
    columnHeadings() {
      return mapHeadings(this.headings, 'heading')
    },

    columnKeys() {
      return mapHeadings(this.headings, 'key')
    },

    dragOptions() {
      return {
        animation: 200
      }
    },

    randomKey() {
      return (index) => {
        let max = 1000
        let min = index

        min = Math.ceil(min)
        max = Math.floor(max)

        return Math.round(Math.random() * (max - min) + min)
      }
    },

    headingClass() {
      return (heading) => {
        return `heading-${heading.toLowerCase()}`
      }
    }
  },

  methods: {
    onRowClick(row) {
      this.$emit('rowClick', row)
    },

    activateDrag() {
      this.drag = true
    },

    orderRows(rows) {
      this.drag = false
      this.$emit('reorderItems', rows)
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/base/theme'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.table
  @extend %card
  padding: 0

  .inner-scroll
    overflow: auto
    -webkit-overflow-scrolling: touch

  table
    width: 100%
    border-collapse: collapse

    &.is-fixed
      table-layout: fixed

  td
    padding: 15px 10px
    border-bottom: 1px dotted $color-border
    vertical-align: middle

    @include themify($themes)
      color: themed('color-body')

    .action
      text-align: center
      padding: 0 10px

    p
      font-size: 16px

    a
      font-family: $font-body

      @include themify($themes)
        color: themed('color-secondary')

    &.drag-handle
      cursor: move!important

      button
        cursor: move

  tr
    &:last-of-type
      td
        border: none

  thead
    td
      padding: 20px 10px
      text-transform: capitalize
      font-weight: bold

  .rows-wrapper
    display: contents

  .drag-handle
    .sprite
      @include themify($themes)
        fill: themed('color-secondary')

  .sortable-chosen
    background: #fff

  .sortable-ghost
    background: #fbfbfb

  .flip-list-move
    transition: transform 0.5s

  .no-move
    transition: transform 0s

  .sortable-wrapper
    .drag-handle
      display: none

    &.is-sortable
      .drag-handle
        display: table-cell

.sprite
  width: 20px
  height: 20px
  fill: #fff
</style>
